import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Button, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, AutoFixHigh as AiIcon } from '@mui/icons-material';
import api from '../services/api';

interface PatternPdfViewerProps {
  latexTemplate: string;
  pythonGenerator: string;
  iterations?: number;
  isReusable?: boolean;
  patternId?: number;
  onPdfGenerated?: () => void;
  onRetryWithAiLatex?: (latexContent: string, errorLogs: string) => void;
  onRetryWithAiPython?: (pythonContent: string, errorLogs: string) => void;
}

const PatternPdfViewer: React.FC<PatternPdfViewerProps> = ({
  latexTemplate,
  pythonGenerator,
  iterations = 3,
  isReusable = true,
  patternId,
  onPdfGenerated,
  onRetryWithAiLatex,
  onRetryWithAiPython
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compilationLogs, setCompilationLogs] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'latex' | 'python' | 'unknown' | null>(null);
  const loadingRef = React.useRef(false);

  useEffect(() => {
    loadPdf();
  }, [latexTemplate, pythonGenerator, iterations, isReusable]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPdf = async () => {
    // Prevent duplicate concurrent PDF generation
    if (loadingRef.current) {
      console.log('PDF generation already in progress, skipping duplicate call');
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      setCompilationLogs(null);
      setErrorType(null);

      // Fetch PDF with POST data
      const response = await api.post('/exercise-patterns/preview_pdf/', {
        latex_template: latexTemplate,
        python_generator: pythonGenerator,
        iterations,
        is_reusable: isReusable,
        pattern_id: patternId,
      }, {
        responseType: 'blob',
      });

      // Check if response is actually an error
      console.log('Pattern PDF Response received:', {
        size: response.data?.size,
        type: response.data?.type,
        status: response.status
      });

      if (!response.data || response.data.size === 0) {
        throw new Error('PDF vide reçu du serveur');
      }

      // The response.data is already a Blob when responseType is 'blob'
      // Don't wrap it in another Blob
      const blob = response.data;

      // Verify it's actually a PDF by checking the first bytes
      const arrayBuffer = await blob.slice(0, 5).arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const pdfSignature = String.fromCharCode.apply(null, Array.from(bytes));

      console.log('Pattern PDF signature:', pdfSignature, 'Expected: %PDF-');

      if (!pdfSignature.startsWith('%PDF')) {
        // Try to read as text to see what we got
        const text = await blob.slice(0, 200).text();
        console.error('Not a PDF! Got:', text);
        throw new Error('Le serveur n\'a pas retourné un PDF valide');
      }

      const objectUrl = URL.createObjectURL(blob);
      console.log('Pattern PDF Blob URL created:', objectUrl, 'Size:', blob.size);
      setPdfUrl(objectUrl);

      // Call callback if provided and pattern ID was sent
      if (onPdfGenerated && patternId) {
        onPdfGenerated();
      }
    } catch (err: any) {
      console.error('Error loading PDF:', err);

      // Try to extract compilation logs and error type from error response
      let logs = null;
      let errorMessage = 'Erreur lors du chargement du PDF';
      let detectedErrorType: 'latex' | 'python' | 'unknown' = 'unknown';

      // Handle blob error response
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorData.detail || errorMessage;

          // Extract logs from various possible keys
          logs = errorData.latex_log || errorData.logs || errorData.compilation_logs || null;

          // If we have error_context, format it nicely
          if (errorData.error_context && Array.isArray(errorData.error_context)) {
            const contextFormatted = errorData.error_context.join('\n\n---\n\n');
            logs = logs ? `${logs}\n\n=== ERREURS DÉTECTÉES ===\n\n${contextFormatted}` : contextFormatted;
          }

          // Detect error type from error message or error_type field
          if (errorData.error_type) {
            detectedErrorType = errorData.error_type;
          } else if (errorMessage.toLowerCase().includes('latex') || errorMessage.toLowerCase().includes('compilation')) {
            detectedErrorType = 'latex';
          } else if (errorMessage.toLowerCase().includes('python') || errorMessage.toLowerCase().includes('generator')) {
            detectedErrorType = 'python';
          }
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }
      } else if (err.response?.data) {
        errorMessage = err.response.data.error || err.response.data.detail || errorMessage;

        // Extract logs from various possible keys
        logs = err.response.data.latex_log || err.response.data.logs || err.response.data.compilation_logs || null;

        // If we have error_context, format it nicely
        if (err.response.data.error_context && Array.isArray(err.response.data.error_context)) {
          const contextFormatted = err.response.data.error_context.join('\n\n---\n\n');
          logs = logs ? `${logs}\n\n=== ERREURS DÉTECTÉES ===\n\n${contextFormatted}` : contextFormatted;
        }

        // Detect error type
        if (err.response.data.error_type) {
          detectedErrorType = err.response.data.error_type;
        } else if (errorMessage.toLowerCase().includes('latex') || errorMessage.toLowerCase().includes('compilation')) {
          detectedErrorType = 'latex';
        } else if (errorMessage.toLowerCase().includes('python') || errorMessage.toLowerCase().includes('generator')) {
          detectedErrorType = 'python';
        }
      }

      setError(errorMessage);
      setCompilationLogs(logs);
      setErrorType(detectedErrorType);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'pattern_preview.pdf';
      link.click();
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" onClick={loadPdf}>
                Réessayer
              </Button>
              {compilationLogs && errorType === 'latex' && onRetryWithAiLatex && (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<AiIcon />}
                  onClick={() => onRetryWithAiLatex(latexTemplate, compilationLogs)}
                >
                  Ré-essayer avec l'IA (LaTeX)
                </Button>
              )}
              {compilationLogs && errorType === 'python' && onRetryWithAiPython && (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<AiIcon />}
                  onClick={() => onRetryWithAiPython(pythonGenerator, compilationLogs)}
                >
                  Ré-essayer avec l'IA (Python)
                </Button>
              )}
            </Box>
          }
        >
          {error}
        </Alert>

        {compilationLogs && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Logs de compilation {errorType === 'latex' ? 'LaTeX' : errorType === 'python' ? 'Python' : ''}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  maxHeight: '400px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {compilationLogs}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  }

  return (
    <Box position="relative" height="100%" minHeight="600px" display="flex" flexDirection="column">
      <Box position="absolute" top={8} right={8} zIndex={1} display="flex" gap={1}>
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          size="small"
          onClick={loadPdf}
        >
          Régénérer
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          size="small"
          onClick={handleDownload}
        >
          Télécharger PDF
        </Button>
      </Box>
      <iframe
        src={pdfUrl || ''}
        width="100%"
        style={{ border: 'none', flex: 1, minHeight: '600px' }}
        title="Pattern Preview PDF"
      />
    </Box>
  );
};

export default PatternPdfViewer;